import { cloneElement, isValidElement, type ReactNode } from 'react';

export interface FormFieldProps {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FormField({
  id,
  label,
  description,
  error,
  children,
  className,
}: FormFieldProps) {
  const control = isValidElement<{
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  }>(children)
    ? cloneElement(children, {
        id: children.props.id ?? id,
        'aria-describedby':
          [
            children.props['aria-describedby'],
            description ? `${id}-description` : '',
            error ? `${id}-error` : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined,
        'aria-invalid': error ? true : children.props['aria-invalid'],
      })
    : children;

  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {label}
      </label>
      {control}
      {description && (
        <p id={`${id}-description`} className="mt-1 text-xs text-ink-subtle">
          {description}
        </p>
      )}
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1 text-xs text-danger-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
